import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import { Box, Button, Chip, CssBaseline, Toolbar, Typography } from '@mui/material';
import LogoutIcon from '@mui/icons-material/Logout';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import AppBar from './AppBar';
import Footer from './Footer';
import FooterContent from './FooterContent';
import Main from './Main';

import { useUserContext } from '../../context/UserContext';
import { useLanguageContext } from '../../hooks/useLanguageContext';
import { multilingual } from '../../multilingual';

const Layout = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { token, username, logout } = useUserContext();
    const { language, changeLanguage } = useLanguageContext();
    const langContent = multilingual[language] ?? multilingual.greek;
    const [dictionary, setDictionary] = useState(langContent?.layout ?? multilingual.greek.layout);

    const handleChange = e => {
        changeLanguage(e.target.value);
    };

    useEffect(() => {
        const content = multilingual[language] ?? multilingual.greek;
        setDictionary(content?.layout ?? multilingual.greek.layout);
    }, [language]);

    const handleLogout = () => {
        logout();
        navigate('/signin');
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', overflowX: 'hidden' }}>
            <CssBaseline />
            <AppBar position="fixed">
                <Toolbar sx={{ minHeight: { xs: 56, sm: 64 }, px: { xs: 1, sm: 2 } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexGrow: 1, minWidth: 0 }}>
                        <Typography
                            variant="h6"
                            component="div"
                            sx={{
                                fontWeight: 600,
                                cursor: 'pointer',
                                '&:hover': { opacity: 0.9 },
                            }}
                            onClick={() => navigate('/')}
                        >
                            Multi-Apartment Management
                        </Typography>
                        {token && username && (
                            <Chip
                                label={username}
                                size="small"
                                sx={{
                                    color: 'white',
                                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                                    border: '1px solid rgba(255, 255, 255, 0.5)',
                                    fontWeight: 500,
                                    '& .MuiChip-label': { px: 1 },
                                }}
                            />
                        )}
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {token && username && (
                            <>
                                <Button
                                    color="inherit"
                                    startIcon={<LogoutIcon />}
                                    onClick={handleLogout}
                                    sx={{ textTransform: 'none' }}
                                >
                                    {dictionary.logout}
                                </Button>
                            </>
                        )}
                        {!token && location.pathname !== '/signin' && (
                            <Button
                                color="inherit"
                                onClick={() => navigate('/signin')}
                                sx={{ textTransform: 'none' }}
                            >
                                {dictionary.login}
                            </Button>
                        )}

                        {/* Language Selector */}
                        <FormControl sx={{ ml: 2, minWidth: 120 }} size="small" className={'language'}>
                            <InputLabel id="demo-select-small-label"
                                sx={{
                                    color: 'white !important',
                                    '&.Mui-focused': {
                                        color: 'white !important',
                                    }
                                }}>
                                {dictionary?.language}
                            </InputLabel>
                            <Select
                                sx={{
                                    color: "white",
                                    '.MuiOutlinedInput-notchedOutline': {
                                        borderColor: 'rgba(255, 255, 255, 1)',
                                    },
                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                        borderColor: 'rgba(255, 255, 255, 1)',
                                    },
                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                        borderColor: 'rgba(255, 255, 255, 1)',
                                    },
                                    '.MuiSvgIcon-root ': {
                                        fill: "white !important",
                                    }
                                }}
                                labelId="demo-select-small-label"
                                id="demo-select-small"
                                value={language === 'english' ? 'en' : language === 'greek' ? 'gr' : language}
                                label={dictionary?.language}
                                onChange={handleChange}
                            >
                                <MenuItem value={'en'}>English</MenuItem>
                                <MenuItem value={'gr'}>Ελληνικά</MenuItem>
                            </Select>
                        </FormControl>
                    </Box>
                </Toolbar>
            </AppBar>

            <Main sx={{ mt: { xs: '56px', sm: '64px' } }}>
                <Outlet />
            </Main>

            <Footer>
                <FooterContent />
            </Footer>
        </Box>
    );
};

export default Layout;
